// Quick filter chips component for enhanced search
import React from 'react';
import { Badge } from './ui/badge';
import { X } from 'lucide-react';
import { fireEvent } from '@/lib/analytics';

export interface ChipFilter {
  id: string;
  label: string;
  active: boolean;
  count?: number;
}

interface ChipGroup {
  id: string;
  label: string;
  chips: ChipFilter[];
}

interface SearchChipsProps {
  chipGroups: ChipGroup[];
  onChipToggle: (groupId: string, chipId: string) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

export default function SearchChips({
  chipGroups,
  onChipToggle,
  onClearAll,
  hasActiveFilters,
}: SearchChipsProps) {
  const handleChipClick = (groupId: string, chipId: string) => {
    const group = chipGroups.find((g) => g.id === groupId);
    const chip = group?.chips.find((c) => c.id === chipId);

    fireEvent('search_chip_toggle', {
      chip: `${groupId}:${chipId}`,
      on: !chip?.active,
    });

    onChipToggle(groupId, chipId);
  };

  const handleClearAll = () => {
    fireEvent('search_clear_all');
    onClearAll();
  };

  return (
    <div className="space-y-4">
      {chipGroups.map((group) => (
        <div key={group.id} className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted-foreground">{group.label}</label>
          </div>
          <div className="flex flex-wrap gap-2">
            {group.chips.map((chip) => (
              <button
                key={chip.id}
                onClick={() => handleChipClick(group.id, chip.id)}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  chip.active
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-secondary/50 text-secondary-foreground hover:bg-secondary'
                }`}
                role="button"
                aria-pressed={chip.active}
              >
                {chip.label}
                {chip.count !== undefined && chip.count > 0 && (
                  <span
                    className={`ml-1.5 text-xs ${
                      chip.active ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    }`}
                  >
                    {chip.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}

      {hasActiveFilters && (
        <div className="pt-2 border-t border-border">
          <button
            onClick={handleClearAll}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md"
          >
            <X className="w-4 h-4 mr-1.5" />
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}
